import {
    Alert,
    AlertTitle,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    IconButton,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { CloseIcon } from 'common';
import useGridContext from 'hooks/grid/useGridContext';
import _ from 'lodash';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import {
    createTemplateLayout,
    createUserLayout,
    layoutsSelectors,
} from 'store/feature/layoutsSlice';
import { userSelectors } from 'store/feature/userSlice';
import { useAppDispatch, useAppSelector } from 'store/hooks';

export interface LayoutSaveAsForm {
    layoutName: string;
    isTemplate: boolean;
}

export interface CreateLayoutDialogProps {
    open: boolean;
    onClose: () => void;
}

const CreateLayoutDialogForm: React.FC<
    CreateLayoutDialogProps & { onSave: (data: LayoutSaveAsForm) => void }
> = ({ open, onClose, onSave }) => {
    const userLayouts = useAppSelector(
        layoutsSelectors.selectedDatasetUserLayouts,
        _.isEqual
    );
    const templateLayouts = useAppSelector(
        layoutsSelectors.selectedDatasetTemplateLayouts,
        _.isEqual
    );
    const isAdmin = useAppSelector(userSelectors.isAdmin);

    const [layoutName, setLayoutName] = useState('');
    const [isSaving, setIsSaving] = useState(false);
    const [isTemplate, setIsTemplate] = useState(false);
    const [errorMessage, setErrorMessage] = useState('Name is required.');

    useEffect(() => {
        if (!layoutName) {
            setErrorMessage('Name is required.');
        } else if (moment(layoutName, moment.ISO_8601, true).isValid()) {
            setErrorMessage('Name is invalid.');
        } else if (isTemplate && !isAdmin) {
            setErrorMessage('You do not have permission to save as template.');
        } else if (
            isTemplate &&
            templateLayouts.find((x) => x.name === layoutName)
        ) {
            setErrorMessage(
                'There is a template layout with this name already.'
            );
        } else if (
            !isTemplate &&
            userLayouts.find((x) => x.name === layoutName)
        ) {
            setErrorMessage('There is a layout with this name already.');
        } else {
            setErrorMessage('');
        }
    }, [layoutName, isTemplate, userLayouts, templateLayouts, isAdmin]);

    const hasErrors = errorMessage.length > 0;

    const handleSave = () => {
        if (hasErrors) {
            return;
        }

        if (isTemplate && !isAdmin) {
            setErrorMessage('You do not have permission to save as template.');
            return;
        }

        setIsSaving(true);
        onSave({ layoutName, isTemplate });
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        e.stopPropagation();
        if (e.key === 'Enter') {
            handleSave();
        } else if (e.key === 'Escape') {
            onClose();
        }
    };
    const handleClose = (e: React.MouseEvent) => {
        e.stopPropagation();
        onClose();
    };

    return (
        <Dialog
            onClick={(e) => e.stopPropagation()}
            onClose={onClose}
            aria-labelledby="LayoutSaveAsDialog_Title"
            open={open}
            PaperProps={{ id: 'LayoutSaveAsDialog', sx: { width: '40%' } }}
        >
            <DialogTitle variant="h4" id="LayoutSaveAsDialog_Title">
                Save As...
            </DialogTitle>
            <IconButton
                aria-label="close"
                onClick={handleClose}
                sx={(theme) => ({
                    position: 'absolute',
                    right: 8,
                    top: 8,
                    color: theme.palette.grey[500],
                })}
            >
                <CloseIcon />
            </IconButton>
            <DialogContent dividers>
                <Stack spacing={1}>
                    <TextField
                        autoFocus
                        fullWidth
                        id="LayoutSaveAsDialog_Input_Name"
                        label="Layout Name"
                        onChange={(e) => setLayoutName(e.target.value)}
                        onKeyDown={handleKeyDown}
                        value={layoutName}
                        error={hasErrors}
                        helperText={errorMessage}
                    />
                    {isAdmin && (
                        <>
                            <FormControl>
                                <FormGroup row>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                value={isTemplate}
                                                onChange={(_, checked) =>
                                                    setIsTemplate(checked)
                                                }
                                            />
                                        }
                                        label="Save as template"
                                    />
                                </FormGroup>
                            </FormControl>
                            {isTemplate && (
                                <Alert severity="warning">
                                    <AlertTitle>Warning</AlertTitle>
                                    Creating a new template layout will affect
                                    <Typography
                                        component="span"
                                        fontWeight={'bold'}
                                    >
                                        {' all users '}
                                    </Typography>
                                    in this dataset.
                                </Alert>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button
                    id="LayoutSaveAsDialog_Cancel"
                    variant="outlined"
                    color="primary"
                    autoFocus
                    onClick={onClose}
                >
                    Cancel
                </Button>
                <Button
                    id="LayoutSaveAsDialog_Save"
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                    disabled={hasErrors || isSaving}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const CreateLayoutDialog: React.FC<CreateLayoutDialogProps> = ({
    open,
    onClose,
}) => {
    const dispatch = useAppDispatch();
    const { gridType } = useGridContext();

    const handleSave = ({ layoutName, isTemplate }: LayoutSaveAsForm) => {
        let task: Promise<any>;
        if (isTemplate) {
            task = dispatch(
                createTemplateLayout({ name: layoutName, gridType })
            );
        } else {
            task = dispatch(createUserLayout({ name: layoutName, gridType }));
        }
        task.then(onClose);
    };

    return (
        <CreateLayoutDialogForm
            open={open}
            onClose={onClose}
            onSave={handleSave}
        />
    );
};

export default CreateLayoutDialog;
